import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, CreditCard, Loader2, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { fmt, QuoteLines, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientPortal() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const tries = useRef(0);

  const load = () =>
    axios.get(`${API}/portal/${token}`).then((r) => setData(r.data)).catch(() => setError("This booking link is invalid or has expired."));

  useEffect(() => { load(); }, [token]);

  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid) return;
    setConfirming(true);
    const timer = setInterval(async () => {
      tries.current += 1;
      try {
        const { data: s } = await axios.get(`${API}/portal/${token}/payment-status/${sid}`);
        if (s.payment_status === "paid") {
          setConfirming(false);
          toast.success("Payment received — thank you!");
          clearInterval(timer);
          load();
        }
      } catch (e) {}
      if (tries.current > 15) { setConfirming(false); clearInterval(timer); }
    }, 2000);
    return () => clearInterval(timer);
  }, [searchParams, token]);

  const pay = async (kind) => {
    setPaying(kind);
    try {
      const { data: res } = await axios.post(`${API}/portal/${token}/pay`, { origin_url: window.location.origin, kind });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to start payment");
      setPaying(null);
    }
  };

  if (error) return <div className="flex min-h-screen items-center justify-center bg-background"><p data-testid="portal-error" className="border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">{error}</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const depositDue = data.amount_paid < data.deposit;
  const depositRemaining = Math.max(0, data.deposit - data.amount_paid);
  const balanceAfterDeposit = Math.max(0, data.balance - depositRemaining);

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-page">
      <header className="border-b border-white/10 bg-ink text-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
          <div className="flex h-9 w-9 items-center justify-center bg-brand"><UtensilsCrossed className="h-5 w-5" /></div>
          <div>
            <p className="font-heading text-sm font-bold">Fleet &amp; Feast Catering</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Client Portal</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-6 py-10">
        {confirming && (
          <div className="flex items-center gap-3 border border-brand bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="portal-confirming">
            <Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment with Stripe…
          </div>
        )}

        <div className="border border-border bg-white p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your event</p>
              <h1 className="mt-1 font-heading text-3xl font-black tracking-tight" data-testid="portal-event-title">{data.customer_name}</h1>
              <p className="mt-1 font-mono text-sm text-muted-foreground">{data.booking_ref}</p>
            </div>
            <StatusBadge status={data.status} />
          </div>
          <dl className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-3">
            <div><dt className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Event date</dt><dd className="mt-1 font-bold tabular-nums" data-testid="portal-date">{data.event_date}</dd></div>
            <div><dt className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Guests</dt><dd className="mt-1 font-bold tabular-nums">{data.guest_count}</dd></div>
            <div><dt className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Venue</dt><dd className="mt-1 font-bold">{data.venue_name || "TBC"}</dd></div>
            {data.menu_name && <div><dt className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Menu</dt><dd className="mt-1 font-bold" data-testid="portal-menu">{data.menu_name}</dd></div>}
          </dl>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="border border-border bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your quote</p>
            <div className="mt-4"><QuoteLines quote={data.quote} /></div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="portal-payments-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Payments</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Paid to date</dt><dd className="font-bold text-brand tabular-nums" data-testid="portal-paid">{fmt(data.amount_paid)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Deposit (25%)</dt><dd className="tabular-nums">{fmt(data.deposit)} {depositDue ? "" : "✓"}</dd></div>
              <div className="flex justify-between border-t border-border pt-2"><dt className="font-heading font-bold">Balance due</dt><dd className="font-heading font-bold tabular-nums" data-testid="portal-balance">{fmt(data.balance)}</dd></div>
            </dl>
            <div className="mt-4 space-y-1.5">
              {data.payments.map((p, i) => (
                <p key={i} className="text-xs text-muted-foreground">✓ {fmt(p.amount)} — {p.payment_type} · {p.at?.slice(0, 10)}</p>
              ))}
            </div>
            {data.balance > 0 && !["cancelled", "completed"].includes(data.status) ? (
              <div className="mt-5 space-y-2">
                {depositRemaining > 0 && (
                  <Button data-testid="portal-pay-deposit" onClick={() => pay("deposit")} disabled={!!paying} className="w-full rounded-md bg-brand hover:bg-brand-dark">
                    <CreditCard className="mr-2 h-4 w-4" /> {paying === "deposit" ? "Redirecting…" : `Pay deposit ${fmt(depositRemaining)}`}
                  </Button>
                )}
                {balanceAfterDeposit > 0.004 && (
                  <Button data-testid="portal-pay-balance" variant="outline" onClick={() => pay("balance")} disabled={!!paying} className="w-full rounded-md border-ink text-ink hover:bg-muted">
                    {paying === "balance" ? "Redirecting…" : `Pay remaining balance ${fmt(balanceAfterDeposit)}`}
                  </Button>
                )}
                {depositRemaining > 0 && (
                  <Button data-testid="portal-pay-full" variant="outline" onClick={() => pay("full")} disabled={!!paying} className="w-full rounded-md border-ink text-ink hover:bg-muted">
                    {paying === "full" ? "Redirecting…" : `Pay in full ${fmt(data.balance)}`}
                  </Button>
                )}
              </div>
            ) : data.balance <= 0 ? (
              <div className="mt-5 flex items-center gap-2 border border-emerald-600 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="portal-paid-full">
                <CheckCircle2 className="h-4 w-4" /> Fully paid — see you at the event!
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
